import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, PhotoIcon } from '@heroicons/react/24/outline';
import BannerCard from '../components/BannerCard';
import BannerModal from '../components/BannerModal';
import ConfirmDialog from '../components/ConfirmDialog';

const BranchLocatorBannersPage = () => {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedBanner, setSelectedBanner] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [bannerToDelete, setBannerToDelete] = useState(null);

    const fetchBanners = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/banners?page_type=branch_locator');
            if (response.data.success) {
                // Double filter on frontend to be safe
                const filtered = (response.data.data || []).filter(
                    banner => banner.page_type === 'branch_locator'
                );
                setBanners(filtered);
            }
        } catch (error) {
            console.error('Error fetching banners:', error);
            toast.error('Failed to load banners');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBanners();
    }, []);

    const handleAdd = () => {
        setSelectedBanner(null);
        setIsModalOpen(true);
    };

    const handleEdit = (banner) => {
        setSelectedBanner(banner);
        setIsModalOpen(true);
    };

    const handleDeleteClick = (id) => {
        setBannerToDelete(id);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            const response = await axios.delete(`/api/admin/banners/${bannerToDelete}`);
            if (response.data.success) {
                toast.success('Banner deleted successfully');
                fetchBanners();
            }
        } catch (error) {
            console.error('Error deleting banner:', error);
            toast.error('Failed to delete banner');
        } finally {
            setIsDeleteModalOpen(false);
            setBannerToDelete(null);
        }
    };

    const handleToggleStatus = async (id, currentStatus) => {
        try {
            const response = await axios.patch(`/api/admin/banners/${id}/status`, {
                is_active: !currentStatus
            });
            if (response.data.success) {
                toast.success(`Banner ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
                fetchBanners();
            }
        } catch (error) {
            console.error('Error toggling banner status:', error);
            toast.error('Failed to update status');
        }
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Branch Locator Banners</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Manage banners for the Branch Locator page
                    </p>
                </div>
                <button
                    onClick={handleAdd}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2.5 rounded-xl font-medium transition-all duration-200 shadow-lg shadow-primary-200"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add New Banner
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
            ) : banners.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {banners.map((banner) => (
                        <BannerCard
                            key={banner.id}
                            banner={banner}
                            onEdit={handleEdit}
                            onDelete={handleDeleteClick}
                            onToggleStatus={handleToggleStatus}
                        />
                    ))}
                </div>
            ) : (
                <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center">
                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <PhotoIcon className="h-8 w-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-1">No banners yet</h3>
                    <p className="text-slate-500 mb-6">Start by adding your first Branch Locator banner</p>
                    <button
                        onClick={handleAdd}
                        className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:text-primary-700"
                    >
                        <PlusIcon className="h-5 w-5" />
                        Add New Banner
                    </button>
                </div>
            )}

            <BannerModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={() => {
                    setIsModalOpen(false);
                    fetchBanners();
                }}
                banner={selectedBanner}
                defaultPageType="branch_locator"
            />

            <ConfirmDialog
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteConfirm}
                title="Delete Banner"
                message="Are you sure you want to delete this banner? This action cannot be undone."
            />
        </div>
    );
};

export default BranchLocatorBannersPage;
